import dayjs from 'dayjs'
import React, {
  useCallback,
  useEffect,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { toast } from 'react-toastify'

import { createMissionCopy, getMission } from '../../api'
import { MissionTypeAPI } from '../../api/types'
import ArrowLeft from '../../assets/icons/arrowLeft.svg'
import { MissionAnswers } from '../../components/MissionAnswers'
import { MissionAssignNinjas } from '../../components/MissionAssignNinjas'
import { MissionDetails } from '../../components/MissionDetails'
import { MissionStatistics } from '../../components/MissionStatistics'
import { CheckIdParamContextType } from '../../components/shared/CheckIdParam/CheckIdParam'
import { Loading } from '../../components/shared/Loading'
import { StoresSummary } from '../../components/StoresSummary'
import { useUser } from '../../contexts/User'
import { Button } from '../../elements/Button'
import { TabIndicator } from '../../elements/TabIndicator'
import { getPaths } from '../../routing/routes'

import styles from './MissionPage.module.scss'


type MissionPageProps = {
  info: 'details' | 'stores_summary' | 'stats' | 'answers' | 'ninjas_assign'
}


export const MissionPage = ({ info }: MissionPageProps) => {
  const i18n = useTranslation()
  const navigate = useNavigate()
  const { hasPermissions } = useUser()
  const { id: missionId } = useOutletContext<CheckIdParamContextType>()

  const [mission, setMission] = useState<MissionTypeAPI>()
  const [creatingMissionCopy, setCreatingMissionCopy] = useState<boolean>(false)


  const goBack = useCallback(() => {
    navigate(getPaths.brands.missions.ongoing)
  }, [navigate])


  useEffect(() => {
    getMission(missionId).then(response => {
      if (!response.success) {
        toast.error(i18n.t('ToastError:MissionNotFound'), { autoClose: 2600, pauseOnHover: false })
        setTimeout(() => {
          goBack()
        }, 3000);
        return
      }

      const mission = response.data

      if (mission.status !== 'created') {
        // handle case if user writes a "custom" url and the mission is still in the creation process
        navigate(getPaths.brands.wizard[mission.status](mission.id))
      }

      setMission(mission)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const handleCreateMissionCopy = useCallback(() => {
    if (!mission) return

    setCreatingMissionCopy(true)
    createMissionCopy(mission.id).then(response => {
      setCreatingMissionCopy(false)
      if (!response.success) {
        toast.error(i18n.t('ToastError:MissionCopyNotCreated'))
        return
      }

      navigate(getPaths.brands.wizard.step_1(response.data.id))
    })
  }, [i18n, navigate, mission])


  return (
    <div>
      <Button
        title={i18n.t('Common:Missions')}
        titleSize='h1'
        icon={ArrowLeft}
        variant='noContainer'
        handler={goBack}
      />
      <div className={styles.top}>
        <h1>
          {mission?.title}
        </h1>
        {hasPermissions('mission_creator') && (
          <Button
            title={i18n.t('Mission:CreateMissionFromThisOne')}
            handler={handleCreateMissionCopy}
            loading={creatingMissionCopy}
          />
        )}
      </div>
      <div>
        {mission && (
          <TabIndicator
            currentTab={info}
            disabled={creatingMissionCopy}
            tabsLeft={[
              {
                id: 'details',
                title: i18n.t('Common:Details'),
                onClick: () => navigate(getPaths.brands.mission.details(mission.id))
              },
              {
                id: 'stores_summary',
                title: i18n.t('Mission:StoresSummary'),
                onClick: () => navigate(getPaths.brands.mission.stores_summary(mission.id))
              },
              {
                id: 'stats',
                title: i18n.t('Common:Statistics'),
                onClick: () => navigate(getPaths.brands.mission.stats(mission.id))
              },
              {
                id: 'answers',
                title: i18n.t('Mission:Answers'),
                onClick: () => navigate(getPaths.brands.mission.answers(mission.id))
              },
              {
                id: 'ninjas_assign',
                title: i18n.t('Mission:NinjasAssign'),
                disabled: dayjs(mission.endDate).isBefore(dayjs()),
                onClick: () => navigate(getPaths.brands.mission.ninjas_assign(mission.id))
              }
            ]}
          />
        )}
      </div>
      {!mission ? (
        <Loading />
      ) : (
        info === 'details' ? (
          <MissionDetails
            mission={mission}
            storesToGet='stores'
          />
        ) : info === 'stores_summary' ? (
          <StoresSummary
            stores={mission.stores || []}
            missionFinished={dayjs(mission.endDate).isBefore(dayjs())}
          />
        ) : info === 'stats' ? (
          <MissionStatistics
            questions={mission.questions || []}
            missionId={mission.id}
          />
        ) : info === 'answers' ? (
          <MissionAnswers
            missionId={mission.id}
            questions={mission.questions || []}
          />
        ) : (
          <MissionAssignNinjas
            missionId={mission.id}
            missionFinished={dayjs(mission.endDate).isBefore(dayjs())}
          />
        )
      )}
    </div>
  )
}
